import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = authService.getToken();
      const storedUser = authService.getUser();
      
      if (token && storedUser) {
        // Verify token is still valid
        const currentUser = await authService.getCurrentUser();
        const userPermissions = await authService.getUserPermissions();
        
        setUser(currentUser);
        setPermissions(userPermissions);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // Token invalid or expired
      await authService.logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { user: userData } = await authService.login(email, password);
      const userPermissions = await authService.getUserPermissions();
      
      setUser(userData);
      setPermissions(userPermissions);
      setIsAuthenticated(true);
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setPermissions([]);
    setIsAuthenticated(false);
  };

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const hasRole = (roleName) => {
    return authService.hasRole(roleName);
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      const userPermissions = await authService.getUserPermissions();
      
      setUser(currentUser);
      setPermissions(userPermissions);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    permissions,
    loading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    hasRole,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;